// UpdatesView.js - Компонент для отображения обновлений программы
import React, {useEffect, useMemo, useState} from "react";
import {
    Button,
    Card,
    Col,
    DatePicker,
    Empty,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Timeline,
    Tooltip,
    Typography
} from "antd";
import {
    AppstoreOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    EyeOutlined,
    FilterOutlined,
    ReloadOutlined,
    RocketOutlined,
    UserOutlined
} from "@ant-design/icons";
import moment from "moment";
import styles from "../updates_log/styles/UpdatesView.module.css";
import {instance_2} from "../../../API/API";

const {Title, Text, Paragraph} = Typography;
const {Option} = Select;
const {RangePicker} = DatePicker;

const UpdatesView = () => {
    const [loading, setLoading] = useState(false);
    const [updates, setUpdates] = useState([]);
    const [total, setTotal] = useState(0);
    const [versions, setVersions] = useState([]);
    const [modules, setModules] = useState([]);
    const [filters, setFilters] = useState({
        version: null,
        module: null,
        dateRange: null
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20
    });

    // Загрузка данных
    const fetchUpdates = async () => {
        setLoading(true);
        try {
            const params = {
                limit: pagination.pageSize,
                offset: (pagination.current - 1) * pagination.pageSize
            };

            if (filters.version) params.version = filters.version;
            if (filters.module) params.module = filters.module;

            const response = await instance_2.get(`develop/get_updates.php`, {params});

            if (response.data && response.data.success) {
                setUpdates(response.data.updates || []);
                setTotal(response.data.total || 0);
                if (response.data.versions_available) {
                    setVersions(response.data.versions_available);
                }
                if (response.data.modules_available) {
                    setModules(response.data.modules_available);
                }
            } else {
                console.error("Failed to load updates:", response.data);
            }
        } catch (error) {
            console.error("Error fetching updates:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUpdates();
    }, [pagination.current, pagination.pageSize, filters.version, filters.module]);

    // Сброс фильтров
    const handleResetFilters = () => {
        setFilters({
            version: null,
            module: null,
            dateRange: null
        });
        setPagination({...pagination, current: 1});
    };

    // Применение фильтра по датам
    const handleDateRangeChange = (dates) => {
        setFilters({...filters, dateRange: dates});
        // Здесь можно добавить фильтрацию по датам на бэкенде
    };

    // Группировка обновлений по версиям
    const groupedByVersion = useMemo(() => {
        const grouped = {};
        updates.forEach(update => {
            if (!grouped[update.version]) {
                grouped[update.version] = [];
            }
            grouped[update.version].push(update);
        });
        return grouped;
    }, [updates]);

    // Получение цвета для модуля
    const getModuleColor = (modulePrefix) => {
        const colors = {
            'API': 'blue',
            'EMP': 'green',
            'STORE': 'orange',
            'PLAN': 'purple',
            'FIN': 'red',
            'ANL': 'cyan',
            'OBJ': 'magenta',
            'EMP2': 'geekblue',
            'COMMON': 'default'
        };
        return colors[modulePrefix] || 'default';
    };

    // Колонки таблицы
    const columns = [
        {
            title: 'ID обновления',
            dataIndex: 'update_id',
            key: 'update_id',
            width: 200,
            render: (text, record) => (
                <Tooltip title={text}>
                    <Tag color="blue" style={{fontFamily: 'monospace', fontSize: '12px'}}>
                        {text}
                    </Tag>
                </Tooltip>
            )
        },
        {
            title: 'Версия',
            dataIndex: 'version',
            key: 'version',
            width: 120,
            render: (text) => (
                <Tag color="purple" icon={<RocketOutlined/>}>
                    {text}
                </Tag>
            )
        },
        {
            title: 'Модуль',
            dataIndex: 'module_prefix',
            key: 'module_prefix',
            width: 150,
            render: (text, record) => (
                <Tag color={getModuleColor(text)}>
                    {record.module_name || text}
                </Tag>
            )
        },
        {
            title: 'Дата',
            dataIndex: 'update_date',
            key: 'update_date',
            width: 120,
            render: (text) => moment(text).format('DD.MM.YYYY')
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            render: (text) => (
                <Paragraph ellipsis={{rows: 2, expandable: true, symbol: 'подробнее'}} style={{margin: 0}}>
                    {text}
                </Paragraph>
            )
        },
        {
            title: 'Автор',
            dataIndex: 'created_by',
            key: 'created_by',
            width: 120,
            render: (text) => (
                <Space>
                    <UserOutlined/>
                    <span>{text || 'System'}</span>
                </Space>
            )
        },
        {
            title: 'Дата создания',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (text) => moment(text).format('DD.MM.YYYY HH:mm')
        }
    ];

    // Статистика
    const statistics = useMemo(() => {
        const uniqueVersions = new Set(updates.map(u => u.version));
        const uniqueModules = new Set(updates.map(u => u.module_prefix));
        const lastUpdate = updates[0]?.update_date;

        return {
            totalUpdates: updates.length,
            totalVersions: uniqueVersions.size,
            totalModules: uniqueModules.size,
            lastUpdateDate: lastUpdate
        };
    }, [updates]);

    return (
        <div className={styles.container}>
            <Card className={styles.headerCard}>
                <Row gutter={[16, 16]} align="middle">
                    <Col flex="auto">
                        <Space direction="vertical" size={0}>
                            <Title level={2} style={{margin: 0}}>
                                <RocketOutlined/> Журнал обновлений программы
                            </Title>
                            <Text type="secondary">
                                История изменений и новых функций системы
                            </Text>
                        </Space>
                    </Col>
                    <Col>
                        <Button
                            icon={<ReloadOutlined/>}
                            onClick={fetchUpdates}
                            loading={loading}
                        >
                            Обновить
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Статистика */}
            <Row gutter={[16, 16]} style={{marginTop: 16}}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Всего обновлений"
                            value={statistics.totalUpdates}
                            prefix={<CheckCircleOutlined/>}
                            valueStyle={{color: '#3f8600'}}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Версий программы"
                            value={statistics.totalVersions}
                            prefix={<RocketOutlined/>}
                            valueStyle={{color: '#1890ff'}}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Модулей с обновлениями"
                            value={statistics.totalModules}
                            prefix={<AppstoreOutlined/>}
                            valueStyle={{color: '#722ed1'}}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title="Последнее обновление"
                            value={statistics.lastUpdateDate ? moment(statistics.lastUpdateDate).format('DD.MM.YYYY') : 'Нет данных'}
                            prefix={<CalendarOutlined/>}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Фильтры */}
            <Card style={{marginTop: 16}}>
                <Space wrap size="middle">
                    <Space>
                        <FilterOutlined/>
                        <Text strong>Фильтры:</Text>
                    </Space>

                    <Select
                        allowClear
                        placeholder="Версия программы"
                        style={{width: 150}}
                        value={filters.version}
                        onChange={(value) => setFilters({...filters, version: value})}
                    >
                        {versions.map(version => (
                            <Option key={version} value={version}>{version}</Option>
                        ))}
                    </Select>

                    <Select
                        allowClear
                        placeholder="Модуль"
                        style={{width: 150}}
                        value={filters.module}
                        onChange={(value) => setFilters({...filters, module: value})}
                    >
                        {modules.map(module => (
                            <Option key={module.module_prefix} value={module.module_prefix}>
                                {module.module_name}
                            </Option>
                        ))}
                    </Select>

                    <RangePicker
                        placeholder={['Дата с', 'Дата по']}
                        onChange={handleDateRangeChange}
                    />

                    <Button onClick={handleResetFilters}>Сбросить</Button>
                </Space>
            </Card>

            {/* Таблица обновлений */}
            <Card style={{marginTop: 16}}>
                <Table
                    columns={columns}
                    dataSource={updates}
                    loading={loading}
                    rowKey="id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: total,
                        showSizeChanger: true,
                        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} обновлений`,
                        onChange: (page, pageSize) => {
                            setPagination({current: page, pageSize});
                        }
                    }}
                    locale={{
                        emptyText: <Empty description="Нет данных об обновлениях"/>
                    }}
                    scroll={{x: 1000}}
                />
            </Card>

            {/* Альтернативное отображение в виде таймлайна (опционально) */}
            {updates.length > 0 && (
                <Card
                    title={
                        <Space>
                            <EyeOutlined/>
                            <span>История изменений (по версиям)</span>
                        </Space>
                    }
                    style={{marginTop: 16}}
                >
                    <Timeline mode="left">
                        {Object.entries(groupedByVersion).map(([version, versionUpdates]) => (
                            <Timeline.Item
                                key={version}
                                label={<Tag color="purple">{version}</Tag>}
                                color="blue"
                            >
                                <Card size="small" style={{marginBottom: 8}}>
                                    {versionUpdates.map(update => (
                                        <div key={update.id} style={{
                                            marginBottom: 12,
                                            paddingBottom: 8,
                                            borderBottom: '1px solid #f0f0f0'
                                        }}>
                                            <Space wrap>
                                                <Tag color={getModuleColor(update.module_prefix)}>
                                                    {update.module_name}
                                                </Tag>
                                                <Tag color="cyan">{update.update_id}</Tag>
                                                <Text type="secondary">
                                                    <CalendarOutlined/> {moment(update.update_date).format('DD.MM.YYYY')}
                                                </Text>
                                                <Text type="secondary">
                                                    <UserOutlined/> {update.created_by || 'System'}
                                                </Text>
                                            </Space>
                                            <Paragraph style={{marginTop: 8, marginBottom: 0}}>
                                                {update.description}
                                            </Paragraph>
                                        </div>
                                    ))}
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                </Card>
            )}
        </div>
    );
};

export default UpdatesView;